import React, { useEffect, useState } from 'react';
import type {
  AhqSubmission,
  AhqSubmissionCandidateExperience,
  Opportunity,
  OpportunityCandidate,
} from '@axiom/validation';
import {
  Accordions,
  Button,
  CondensedHeader,
  CondensedMedium,
  DateUtil,
  EmptyLayout,
  Flashy,
  FluidButtonLayout,
  Form,
  FormLabel,
  Grid,
  GridColumn,
  Gutter,
  Icon,
  IconButton,
  Layout,
  LayoutItem,
  ParagraphHeader,
  ReadonlyTextarea,
  Textarea,
  TilesItem,
  useApi,
  Well,
} from '@axiom/ui';

import NoResults from '../../public/svg/no-results-binoculars.svg';
import EmptyProject from '../../public/svg/empty-project.svg';
import { PriorSubmissionsApi } from '../../api/prior-submissions-api';
import { SubmissionsLegacyApi } from '../../api/submissions-legacy-api';

import { OpportunitySubmissionCandidateExperienceItem } from './OpportunitySubmissionCandidateExperienceItem';
import { OpportunitySubmissionCandidateExperiencesSchema } from './opportunity-submission-candidate-experiences-schema';
import { OpportunitySubmissionUtil } from './opportunity-submission-util';

export type OpportunitySubmissionCandidateExperiencesProps = {
  candidate: OpportunityCandidate;
  experiences: AhqSubmissionCandidateExperience[];
  name?: string;
  opportunity: Opportunity;
  readOnly: boolean;
  submission: AhqSubmission;
  onSubmissionDescriptionSave?: (
    candidateId: string,
    description: string
  ) => void;
};

export const OpportunitySubmissionCandidateExperiences = ({
  experiences,
  candidate,
  name = 'SUBMISSION_EXPERIENCE',
  opportunity,
  readOnly,
  submission,
  onSubmissionDescriptionSave,
}: OpportunitySubmissionCandidateExperiencesProps) => {
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [selectedPriorIndex, setSelectedPriorIndex] = useState<number | null>(
    null
  );

  const [{ data: priorSubmissionsData }] = useApi(
    PriorSubmissionsApi.readPriorSubmissions(
      submission.submissionId,
      candidate.id
    )
  );

  const { aiDraftSubmission, priorSubmissions, talentSummaryBio } =
    priorSubmissionsData;

  const getSelectedPrior = () => {
    return selectedPriorIndex === null || selectedPriorIndex <= 0
      ? null
      : (priorSubmissions[selectedPriorIndex - 1] ?? null);
  };

  const selectedPrior = getSelectedPrior();
  const isAiDraftSelected = selectedPriorIndex === -1;
  const isTalentSummarySelected = selectedPriorIndex === 0;
  const hasSelection = selectedPriorIndex !== null;

  /**
   * We need to always pull fresh data when we load this component
   */
  useEffect(() => {
    void PriorSubmissionsApi.refreshPriorSubmissions(
      submission.submissionId,
      candidate.id
    );
  }, []);

  const onEyeballClicked = async (
    experienceVisible: boolean,
    experienceId: string
  ) => {
    const updatedHighlightedExperiences = experiences
      .map(experience =>
        experience.id === experienceId
          ? { ...experience, experienceVisible }
          : experience
      )
      .map(exp => OpportunitySubmissionUtil.prepareExperienceForPatch(exp));
    await SubmissionsLegacyApi.updateSubmission(submission.submissionId, {
      newSubmissionType: submission.newSubmissionType,
      candidates: [
        {
          candidateId: candidate.id,
          highlightedExperiences: updatedHighlightedExperiences,
        },
      ],
    });
    await OpportunitySubmissionUtil.refreshSubmissionAndCandidates({
      submissionId: submission.submissionId,
      opportunityId: opportunity.id,
      candidateId: candidate.id,
    });
  };

  const handleExperienceDescriptionSave = async (
    submissionDescription: string | null,
    experienceId: string
  ) => {
    await SubmissionsLegacyApi.updateSubmission(submission.submissionId, {
      newSubmissionType: submission.newSubmissionType,
      candidates: [
        {
          candidateId: candidate.id,
          highlightedExperiences: experiences
            .map(experience =>
              experience.id === experienceId
                ? {
                    ...experience,
                    submissionDescription: submissionDescription || '',
                  }
                : experience
            )
            .map(exp =>
              OpportunitySubmissionUtil.prepareExperienceForPatch(exp)
            ),
        },
      ],
    });
    await OpportunitySubmissionUtil.refreshSubmissionAndCandidates({
      submissionId: submission.submissionId,
      opportunityId: opportunity.id,
      candidateId: candidate.id,
    });
  };

  const onStarClicked = async (
    isHighlighted: boolean,
    experienceId: string
  ) => {
    const updatedHighlightedExperiences = experiences
      .map(experience =>
        experience.id === experienceId
          ? { ...experience, isHighlighted }
          : experience
      )
      .map(exp => OpportunitySubmissionUtil.prepareExperienceForPatch(exp));
    await SubmissionsLegacyApi.updateSubmission(submission.submissionId, {
      newSubmissionType: submission.newSubmissionType,
      candidates: [
        {
          candidateId: candidate.id,
          highlightedExperiences: updatedHighlightedExperiences,
        },
      ],
    });
    await OpportunitySubmissionUtil.refreshSubmissionAndCandidates({
      submissionId: submission.submissionId,
      opportunityId: opportunity.id,
      candidateId: candidate.id,
    });
  };

  const visibleExperienceIds = new Set(
    experiences.filter(e => e.experienceVisible).map(e => e.id)
  );

  const hiddenExperiences = experiences.filter(
    exp => !visibleExperienceIds.has(exp.id)
  );

  const readonlyDescriptionContent = candidate.submissionDescription ? (
    <ReadonlyTextarea
      name="READONLY_SUBMISSION_DESCRIPTION"
      value={candidate.submissionDescription}
      formatting="modern"
    />
  ) : (
    <EmptyLayout
      graphic={<EmptyProject width="117px" height="117px" />}
      name="NO_SUBMISSION_DETAILS"
    >
      <Layout position="middle" stretched>
        <LayoutItem>
          <em>Include talent in the Submission and customize their details</em>
        </LayoutItem>
      </Layout>
    </EmptyLayout>
  );

  return (
    <div data-test={name}>
      <Layout position="middle space-between">
        {!isEditingDescription && (
          <FormLabel name="SUBMISSION_DETAILS_LABEL">
            Submission Details
          </FormLabel>
        )}
        {!readOnly && !isEditingDescription && (
          <IconButton
            icon="pencil"
            pattern="secondary"
            variation="minimal"
            onClick={() => {
              setIsEditingDescription(true);
            }}
            name="EDIT_SUBMISSION_DESCRIPTION_BUTTON"
          />
        )}
      </Layout>
      {isEditingDescription ? (
        <Form
          name="EDIT_SUBMISSION_DESCRIPTION"
          schema={OpportunitySubmissionCandidateExperiencesSchema}
          initialValues={{
            submissionDescription: candidate.submissionDescription || '',
          }}
          onSubmit={(_, __, values) => {
            onSubmissionDescriptionSave?.(
              candidate.id,
              values.submissionDescription
            );
            setIsEditingDescription(false);
            setSelectedPriorIndex(null);
          }}
        >
          {({ fireSubmit }) => (
            <Grid>
              <GridColumn largeScreenWidth={hasSelection ? 8 : 12}>
                <Textarea
                  name="submissionDescription"
                  id={`candidate-${candidate.id}-submissionDescription`}
                  label="Submission Details"
                  full
                  formatting="modern"
                  enableSuggestionsFor="envoy-OpportunitySubmissionCandidateExperiences-submissionDescription"
                />
                <Gutter bottom="16px" />
                <FluidButtonLayout>
                  <Button
                    name="CANCEL_SUBMISSION_DESCRIPTION"
                    onClick={() => {
                      setIsEditingDescription(false);
                      setSelectedPriorIndex(null);
                    }}
                    variation="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    name="SAVE_SUBMISSION_DESCRIPTION"
                    type="submit"
                    onClick={fireSubmit}
                  >
                    Save
                  </Button>
                </FluidButtonLayout>
                <Gutter bottom="16px" />
                <CondensedMedium>
                  Or select from previous submissions
                </CondensedMedium>
                <Gutter bottom="8px" />
                <Layout
                  wrap
                  horizontalGutter="8px"
                  verticalGutter="8px"
                  name="PRIOR_SUBMISSIONS_TILES"
                >
                  {aiDraftSubmission ? (
                    <LayoutItem>
                      <TilesItem
                        name="PRIOR_TILE_AI_DRAFT"
                        selected={isAiDraftSelected}
                        onClick={() => {
                          setSelectedPriorIndex(-1);
                        }}
                      >
                        <Layout position="middle center" horizontalGutter="8px">
                          <Icon name="intelligence" />
                          {aiDraftSubmission.name}
                        </Layout>
                      </TilesItem>
                    </LayoutItem>
                  ) : null}
                  <LayoutItem>
                    <TilesItem
                      name="PRIOR_TILE_DEFAULT_BIO"
                      selected={isTalentSummarySelected}
                      onClick={() => {
                        setSelectedPriorIndex(0);
                      }}
                    >
                      <Layout position="middle center" horizontalGutter="8px">
                        <Icon name="avatar" />
                        {talentSummaryBio.name}
                      </Layout>
                    </TilesItem>
                  </LayoutItem>
                  {priorSubmissions.map((prior, index) => (
                    <LayoutItem
                      key={`prior-${prior.name}-${prior.publishedAtDate}`}
                    >
                      <TilesItem
                        name={`PRIOR_TILE_${index}`}
                        selected={selectedPriorIndex === index + 1}
                        onClick={() => {
                          setSelectedPriorIndex(index + 1);
                        }}
                        className="max-lines-1"
                      >
                        {prior.name}
                      </TilesItem>
                    </LayoutItem>
                  ))}
                </Layout>
              </GridColumn>
              {hasSelection ? (
                <GridColumn largeScreenWidth={4} stretched>
                  <Well name="PRIOR_SUBMISSION_PREVIEW">
                    {isAiDraftSelected && aiDraftSubmission ? (
                      <>
                        <Layout position="middle" horizontalGutter="8px">
                          <Icon name="intelligence-filled" />
                          <ParagraphHeader name="PRIOR_SUBMISSION_PREVIEW_NAME">
                            {aiDraftSubmission.name}
                          </ParagraphHeader>
                        </Layout>
                        <Gutter bottom="16px" />
                        <CondensedMedium name="PRIOR_SUBMISSION_PREVIEW_DATE">
                          <Flashy bold>Generated:</Flashy>{' '}
                          {DateUtil.displayLongMonthDayYear(
                            aiDraftSubmission.receivedAt
                          ) ?? '--'}
                        </CondensedMedium>
                        <Gutter bottom="8px" />
                        <ReadonlyTextarea
                          name="PRIOR_SUBMISSION_PREVIEW_SUMMARY"
                          value={aiDraftSubmission.summary}
                        />
                      </>
                    ) : null}
                    {isTalentSummarySelected ? (
                      <>
                        <Layout position="middle" horizontalGutter="8px">
                          <Icon name="avatar-filled" />
                          <ParagraphHeader name="PRIOR_SUBMISSION_PREVIEW_NAME">
                            {talentSummaryBio.name}
                          </ParagraphHeader>
                        </Layout>
                        <Gutter bottom="16px" />
                        <CondensedMedium name="PRIOR_SUBMISSION_PREVIEW_DATE">
                          <Flashy bold>Updated:</Flashy>{' '}
                          {DateUtil.displayDate(
                            talentSummaryBio.candidateSummaryUpdatedAt
                          ) ?? '--'}
                        </CondensedMedium>
                        <Gutter bottom="8px" />
                        <ReadonlyTextarea
                          name="PRIOR_SUBMISSION_PREVIEW_SUMMARY"
                          value={talentSummaryBio.candidateSummary ?? '--'}
                        />
                      </>
                    ) : null}
                    {selectedPrior ? (
                      <>
                        <CondensedHeader name="PRIOR_SUBMISSION_PREVIEW_NAME">
                          {selectedPrior.name ?? '--'}
                        </CondensedHeader>
                        <CondensedMedium name="PRIOR_SUBMISSION_PREVIEW_DATE">
                          <Flashy bold>Published:</Flashy>{' '}
                          {DateUtil.displayDate(
                            selectedPrior.publishedAtDate
                          ) ?? '--'}
                        </CondensedMedium>
                        <CondensedMedium name="PRIOR_SUBMISSION_PREVIEW_FOCUS_AREA">
                          <Flashy bold>Focus Area:</Flashy>{' '}
                          {selectedPrior.focusArea ?? '--'}
                        </CondensedMedium>
                        <CondensedMedium name="PRIOR_SUBMISSION_PREVIEW_INDUSTRY">
                          <Flashy bold>Industry:</Flashy>{' '}
                          {selectedPrior.industry ?? '--'}
                        </CondensedMedium>
                        <CondensedMedium name="PRIOR_SUBMISSION_PREVIEW_OWNER">
                          <Flashy bold>Owner:</Flashy>{' '}
                          {selectedPrior.ownerUser.fullName ?? '--'}
                        </CondensedMedium>
                        <Gutter bottom="8px" />
                        <ReadonlyTextarea
                          name="PRIOR_SUBMISSION_PREVIEW_SUMMARY"
                          value={selectedPrior.summary ?? '--'}
                        />
                      </>
                    ) : null}
                  </Well>
                </GridColumn>
              ) : null}
            </Grid>
          )}
        </Form>
      ) : (
        readonlyDescriptionContent
      )}
      {candidate.isActiveForSubmission ? (
        <>
          <Gutter vertical="24px">
            <CondensedHeader name="INCLUDED_EXPERIENCES_HEADING">
              Included Experiences
            </CondensedHeader>
            <Gutter bottom="8px" />
            <CondensedMedium>
              The following experiences will be included in your submission. You
              can edit or hide an experience from this submission. All changes
              will apply only to this submission.
            </CondensedMedium>
          </Gutter>
          {experiences.filter(exp => visibleExperienceIds.has(exp.id))
            .length === 0 ? (
            <EmptyLayout
              graphic={<NoResults width="117px" height="117px" />}
              name="NO_INCLUDED_EXPERIENCES"
            >
              <Layout position="middle" stretched>
                <LayoutItem>
                  <em>No included experiences</em>
                </LayoutItem>
              </Layout>
            </EmptyLayout>
          ) : (
            <Accordions name="EXPERIENCES">
              {experiences
                .filter(exp => visibleExperienceIds.has(exp.id))
                .map(experience => (
                  <OpportunitySubmissionCandidateExperienceItem
                    key={`visible-${experience.id}`}
                    experience={experience}
                    readOnly={readOnly}
                    eyeOpen
                    isHighlighted={experience.isHighlighted}
                    onEyeballClicked={async () => {
                      await onEyeballClicked(false, experience.id);
                    }}
                    onStarClicked={async () => {
                      await onStarClicked(
                        !experience.isHighlighted,
                        experience.id
                      );
                    }}
                    onExperienceSubmissionDescriptionChanged={async (
                      desc,
                      expId
                    ) => {
                      await handleExperienceDescriptionSave(desc, expId);
                    }}
                    candidateId={candidate.id}
                  />
                ))}
            </Accordions>
          )}
          {hiddenExperiences.length > 0 && (
            <>
              <Gutter bottom="24px" />
              <Flashy color="contentBreak">
                <hr />
              </Flashy>
              <Gutter bottom="24px" />
              <CondensedHeader>Hidden Experiences</CondensedHeader>
              <Gutter bottom="8px" />
              <CondensedMedium>
                The following experiences have been hidden from this submission.
                You can include a hidden experience by clicking the
                &quot;unhide&quot; icon.
              </CondensedMedium>
              <Gutter bottom="24px" />
              <Accordions name="HIDDEN_EXPERIENCES">
                {hiddenExperiences.map(experience => (
                  <OpportunitySubmissionCandidateExperienceItem
                    key={`hidden-${experience.id}`}
                    experience={experience}
                    readOnly={readOnly}
                    onEyeballClicked={async () => {
                      await onEyeballClicked(true, experience.id);
                    }}
                    onExperienceSubmissionDescriptionChanged={async (
                      desc,
                      expId
                    ) => {
                      await handleExperienceDescriptionSave(desc, expId);
                    }}
                    candidateId={candidate.id}
                  />
                ))}
              </Accordions>
            </>
          )}
        </>
      ) : null}
    </div>
  );
};
